import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MessageSquare, Search, ShieldCheck, Plane, Truck, ArrowUpRight } from "lucide-react";
import PageHeader from "../components/layout/PageHeader";
import FAQAccordion from "../components/features/FAQAccordion";
import { setPageMeta, JsonLd, breadcrumbSchema, faqSchema } from "../lib/schema";

const STEPS = [
  {
    icon: MessageSquare,
    n: "01",
    title: "Send us your request",
    body:
      "WhatsApp, email, or our order form. A product link is ideal — if you don't have one, send a screenshot, a brand name, or just a description. A real human concierge will read it.",
  },
  {
    icon: Search,
    n: "02",
    title: "We quote and confirm",
    body:
      "Within a few hours, we send you a confirmed quote covering product price, service fee, and freight to your Nigerian state. No hidden surcharges, no surprises.",
  },
  {
    icon: ShieldCheck,
    n: "03",
    title: "You pay, we purchase",
    body:
      "Once you approve the quote, you pay via bank transfer. We purchase the item from the UK retailer and direct it to our consolidation point in London.",
  },
  {
    icon: Plane,
    n: "04",
    title: "Quality check & dispatch",
    body:
      "We inspect every parcel, photograph the contents, and confirm they match your order before international shipment. Luxury items receive in-store verification.",
  },
  {
    icon: Truck,
    n: "05",
    title: "Delivered to your door",
    body:
      "Air freight to Lagos with onward delivery to any of the 36 states. Typically 7 to 10 working days. You get tracking updates at every leg.",
  },
];

const FAQ_LITE = [
  {
    question: "How long does the whole process take?",
    answer:
      "From your initial enquiry to delivery, expect 8 to 12 working days. Quoting is typically within a few hours of receiving your request.",
  },
  {
    question: "Do I need a UK address or card?",
    answer:
      "No. We use our own UK consolidation point and pay from verified UK accounts. You only need a Nigerian delivery address.",
  },
  {
    question: "What payment methods do you accept?",
    answer:
      "Bank transfer (Nigerian or international). Payment is taken only after we have confirmed your quote.",
  },
];

export default function HowItWorksPage() {
  useEffect(() => {
    setPageMeta({
      title: "How to Buy from the UK and Ship to Nigeria | MPS",
      description:
        "Five simple steps from request to delivery. How My Private Shopper sources, verifies, and ships UK products to any Nigerian state.",
      canonical: "https://privateshopper.co.uk/how-it-works",
    });
  }, []);

  return (
    <>
      <JsonLd data={breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "How It Works" },
      ])} />
      <JsonLd data={faqSchema(FAQ_LITE)} />
      <PageHeader
        overline="How It Works"
        title={<>From London to Lagos in <span className="italic text-[#C9A96E]">five steps</span>.</>}
        body="A real concierge handles every order end-to-end. No automated forwarders, no marketplaces, no guesswork."
      />

      <section className="container-mps py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`how-step-${s.n}`}
            >
              <div className="font-serif text-6xl text-[#C9A96E]/25 leading-none mb-4">{s.n}</div>
              <s.icon className="text-[#C9A96E] mb-4" size={24} strokeWidth={1.4} />
              <h3 className="font-serif text-2xl mb-3">{s.title}</h3>
              <p className="text-sm text-[#6B7280] leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="bg-[#0A0A0A] text-[#F8F5F0] py-24">
        <div className="container-mps text-center max-w-3xl mx-auto">
          <div className="overline text-[#C9A96E] mb-4">Common Questions</div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight">
            Quick answers, <span className="italic text-[#C9A96E]">no jargon</span>.
          </h2>
        </div>
        <div className="container-mps max-w-3xl mx-auto mt-14 bg-[#F8F5F0] text-[#1A1A1A] p-8 md:p-12">
          <FAQAccordion items={FAQ_LITE} testIdPrefix="howit-faq" />
        </div>
      </section>

      <section className="bg-[#F8F5F0] py-24 text-center">
        <div className="container-mps">
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight">
            Ready to begin?
          </h2>
          <p className="mt-6 text-[#6B7280] max-w-xl mx-auto">
            Send your first request now — we&apos;ll quote in hours.
          </p>
          <Link
            to="/order"
            data-testid="howit-final-cta"
            className="mt-10 inline-flex items-center gap-2 px-8 py-4 bg-[#C9A96E] text-[#0A0A0A] text-sm uppercase tracking-[0.2em] hover:bg-[#A68A52]"
          >
            Start Your Order <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </>
  );
}
